import { motion, useScroll, useTransform } from "framer-motion";
import { CalendarDays, MapPin, Timer, ArrowUpRight } from "lucide-react";
import { EVENT, IMAGES, LOGO_URL } from "@/config";
import { scrollToId } from "@/components/Header";

const lineVariants = {
  hidden: { y: "115%" },
  show: (i) => ({
    y: "0%",
    transition: { delay: 0.25 + i * 0.14, duration: 1, ease: [0.16, 1, 0.3, 1] },
  }),
};

const MaskedLine = ({ index, children }) => (
  <span className="block overflow-hidden pb-[0.08em]">
    <motion.span
      className="block"
      custom={index}
      variants={lineVariants}
      initial="hidden"
      animate="show"
    >
      {children}
    </motion.span>
  </span>
);

export const Hero = () => {
  const { scrollY } = useScroll();
  const yBg = useTransform(scrollY, [0, 900], [0, 110]);
  const opacityMeta = useTransform(scrollY, [0, 500], [1, 0]);

  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative flex min-h-screen flex-col justify-end overflow-hidden bg-[#062012] text-white"
    >
      <motion.div style={{ y: yBg }} className="absolute inset-0">
        <img
          src={IMAGES.hero}
          alt="Joyful runners at the 2Run4Hope community race"
          className="h-[115%] w-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#062012]/95 via-[#062012]/55 to-[#062012]/50" />
      </motion.div>

      <div className="noise-overlay pointer-events-none absolute inset-0 opacity-20" />

      <motion.div
        initial={{ opacity: 0, scale: 0.5, rotate: -14 }}
        animate={{ opacity: 1, scale: 1, rotate: 0 }}
        transition={{ delay: 1.05, duration: 1, ease: [0.16, 1, 0.3, 1] }}
        className="absolute right-6 top-28 z-10 md:right-12 md:top-32 lg:right-20 lg:top-36"
      >
        <img
          src={LOGO_URL}
          alt="Official 2Run4Hope race logo"
          data-testid="hero-logo-badge"
          className="w-28 animate-float-slow rounded-full shadow-[0_0_70px_rgba(52,209,107,0.45)] ring-4 ring-white/25 md:w-40 lg:w-52"
        />
      </motion.div>

      <div className="relative z-10 mx-auto w-full max-w-7xl px-4 pb-16 pt-40 md:px-8 md:pb-24">
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15, duration: 0.7 }}
          data-testid="hero-overline"
          className="mb-6 flex items-center gap-3 text-xs font-semibold uppercase tracking-[0.3em] text-hope-bright md:text-sm"
        >
          <span className="inline-block h-2 w-2 rounded-full bg-hope-red" />
          {EVENT.org} Fundraiser
        </motion.p>

        <h1 className="font-heading font-black leading-[0.85] tracking-tighter">
          <span data-testid="hero-title-line-1" className="block text-[clamp(4.5rem,17vw,13rem)]">
            <MaskedLine index={0}>2RUN</MaskedLine>
          </span>
          <span data-testid="hero-title-line-2" className="block text-[clamp(4.5rem,17vw,13rem)]">
            <MaskedLine index={1}>
              <span className="text-hope-bright">4</span>
              <span className="text-hope-red">HOPE</span>
            </MaskedLine>
          </span>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.75, duration: 0.8 }}
          data-testid="hero-subtitle"
          className="mt-6 max-w-xl text-base leading-relaxed text-white/90 md:text-lg"
        >
          A chip-timed 5K run and 1 mile fun walk for National Hunger &amp;
          Homelessness Awareness Week. Every step funds transitional housing for
          families in crisis.
        </motion.p>

        <motion.div
          style={{ opacity: opacityMeta }}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9, duration: 0.8 }}
          className="mt-10 flex flex-col gap-6 md:flex-row md:items-end md:justify-between"
        >
          <div data-testid="hero-event-meta" className="flex flex-wrap gap-x-8 gap-y-3 text-sm text-white/85">
            <span className="flex items-center gap-2">
              <CalendarDays className="h-4 w-4 text-hope-bright" />
              {EVENT.date}
            </span>
            <span className="flex items-center gap-2">
              <Timer className="h-4 w-4 text-hope-bright" />
              {EVENT.time} Start
            </span>
            <span className="flex items-center gap-2">
              <MapPin className="h-4 w-4 text-hope-bright" />
              {EVENT.venue}, Allen TX
            </span>
          </div>

          <div className="flex flex-wrap items-center gap-4">
            <a
              href="#register-widget"
              onClick={scrollToId("#register-widget")}
              data-testid="hero-register-btn"
              className="group inline-flex items-center gap-2 rounded-full bg-hope-green px-8 py-4 font-heading text-base font-bold text-white transition-all duration-300 hover:-translate-y-1 hover:bg-white hover:text-hope-ink"
            >
              Register Now
              <ArrowUpRight className="h-5 w-5 transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
            </a>
            <a
              href="#why"
              onClick={scrollToId("#why")}
              data-testid="hero-why-btn"
              className="inline-flex items-center gap-2 rounded-full border border-white/30 px-8 py-4 font-heading text-base font-bold text-white transition-all duration-300 hover:border-white hover:bg-white/10"
            >
              Why We Run
            </a>
          </div>
        </motion.div>
      </div>
    </section>
  );
};
